import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Report, ReportTypes } from '../../../models/Analytics/types'
import { BaseUser } from '../../../models/Users/types'
import { defaultPermissible } from '../../../models/Permissions/types'
import { axiosInstance } from '../../../helpers'

const REPORTS_ENDPOINT = '/stats/audit-reports/'

// Extended interface to include filter options
interface GenerateReportOptions {
    name: string
    report_type: ReportTypes
    filters?: {
        organization_id?: number
        apartment_ids?: number[]
        accountManagerId?: number
        startDate?: string
        endDate?: string
        includeArchived?: boolean
    }
}

// Paginated response interface
interface PaginatedResponse<T> {
    count: number
    next: string | null
    previous: string | null
    results: T[]
}

// Pagination parameters interface
interface PaginationParams {
    page?: number
    page_size?: number
}

// Function to fetch reports from the API with pagination
const fetchReports = async (
    params: PaginationParams = {},
): Promise<PaginatedResponse<Report>> => {
    const { data } = await axiosInstance.get(REPORTS_ENDPOINT, {
        params: {
            page: params.page || 1,
            page_size: params.page_size || 20,
        },
    })
    return data
}

// Function to generate a new report
const generateReport = async (
    reportData: GenerateReportOptions,
): Promise<Report> => {
    const { data } = await axiosInstance.post(
        `stats/reports/generate-audit-report/`,
        reportData,
    )
    return data
}

// Function to delete a report
const deleteReport = async (reportId: number): Promise<void> => {
    await axiosInstance.delete(`${REPORTS_ENDPOINT}/${reportId}`)
}

export const useReports = (paginationParams: PaginationParams = {}) => {
    const queryClient = useQueryClient()

    // Query to fetch paginated reports
    const {
        data: paginatedData,
        isLoading,
        isError,
        error,
        refetch,
    } = useQuery<PaginatedResponse<Report>, Error>({
        queryKey: ['reports', paginationParams],
        queryFn: () => fetchReports(paginationParams),
        staleTime: 1000 * 60 * 5, // 5 minutes
    })

    // Extract reports and pagination info from the response
    const reports = paginatedData?.results || []
    const totalCount = paginatedData?.count || 0
    const hasNextPage = !!paginatedData?.next
    const hasPreviousPage = !!paginatedData?.previous
    const currentPage = paginationParams.page || 1

    // Mutation to generate a new report
    const generateReportMutation = useMutation({
        mutationFn: generateReport,
        onSuccess: () => {
            // Invalidate and refetch reports list after a new report is generated
            queryClient.invalidateQueries({ queryKey: ['reports'] })
        },
    })

    // Mutation to delete a report
    const deleteReportMutation = useMutation({
        mutationFn: deleteReport,
        onSuccess: () => {
            // Invalidate and refetch reports list after a report is deleted
            queryClient.invalidateQueries({ queryKey: ['reports'] })
        },
    })

    // Handle downloading report files
    const handleDownloadReport = (report: Report) => {
        if (report.xlsx_report) {
            // For xlsx reports, use the AWS URL directly
            const link = document.createElement('a')
            link.href = report.xlsx_report
            link.download = `${report.name}.xlsx`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        } else {
            console.log('No XLSX report available to download')
        }
    }

    return {
        reports,
        totalCount,
        hasNextPage,
        hasPreviousPage,
        currentPage,
        isLoading,
        isError,
        error,
        refetch,
        generateReport: generateReportMutation.mutate,
        isGenerating: generateReportMutation.isLoading,
        deleteReport: deleteReportMutation.mutate,
        isDeleting: deleteReportMutation.isLoading,
        downloadReport: handleDownloadReport,
    }
}
